import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import axios, { AxiosError } from 'axios';
import { useGoogleLogin } from '@react-oauth/google';
import { useSnackbar } from 'notistack';

import { User, AuthStatus } from '../types';

type AuthContextType = {
  api: any;
  status: AuthStatus;
  user: User | null;
  login_google: () => void;
  login_email: Function;
  logout: () => Promise<void>;
  error: string;
  setError: Function;
  // refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string>('');

  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  // Optional: set once (so you don’t repeat baseURL everywhere)
  const api = useMemo(() => {
    return axios.create({
      baseURL: process.env.REACT_APP_SERVER_URL,
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    });
  }, []);

  const login_google = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: async ({ code }) => {
      try {
        setError('');
        setStatus('loading');
        // console.log('google login success!');
        // console.log('code', code);
        const res = await api.post('/auth/google', { code });
        // console.log('res', res);
        // expect { ok: true, user: {...} }
        setUser(res.data.user);
        setStatus('logged_in');
        navigate(-1);
        enqueueSnackbar('登入成功！', { variant: 'success' });
      } catch (err) {
        setUser(null);
        setStatus('logged_out');
        if (axios.isAxiosError(err)) displayErrMsg(err, setError);
      }
    },
    onError: () => {
      setUser(null);
      setStatus('logged_out');
    },
  });

  const login_email = useCallback(async (code: string) => {
    // console.log('input in authcontext', code);
    try {
      setError('');
      setStatus('loading');
      const res = await api.post('/auth/email', { code });
      // console.log('res email', res);
      setUser(res.data.user);
      setStatus('logged_in');
      navigate('/');
      enqueueSnackbar('登入成功！', { variant: 'success' });
    } catch (err) {
      setUser(null);
      setStatus('logged_out');
      if (axios.isAxiosError(err)) displayErrMsg(err, setError);
    }
  }, [])

  const logout = useCallback(async () => {
    setStatus('loading');
    try {
      await api.post('/auth/logout', {});
    } finally {
      setUser(null);
      setStatus('logged_out');
    }
  }, []);

  useEffect(() => {
    // login_email();
    // App startup
    const get_user = async () => {
      try {
        const res = await api.get('/auth/me');
        if (res.data.user) {
          setUser(res.data.user);
          setStatus('logged_in');
        } else {
          setUser(null);
          setStatus('logged_out');
        }
      } catch (err) {
        console.log('Something wrong with /auth/me');
      }
    }
    get_user();
  }, []);

  // Will update this in the future
  // const refresh = async () => {
    
  // };

  const value = useMemo<AuthContextType>(
    () => ({
      api,
      status,
      user,
      login_google,
      login_email,
      logout,
      error,
      setError,
      // refresh,
    }), [status, user, login_google, login_email, logout, error, setError]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

const displayErrMsg = (err: AxiosError, setError: Function) => {
  const status = err.response?.status;
  // const message = err.response?.data?.error;

  switch (status) {
    case 400:
      setError('Invalid request');
      break;
    case 401:
      setError('Invalid login credentials');
      break;
    case 403:
      setError('You do not have access');
      break;
    case 500:
    default:
      setError('Something went wrong. Please try again later.');
  }
}